// Captures uncaught errors and promise rejections during app startup so a
// boot-time whitescreen has a cause attached in telemetry. The ErrorBoundary
// only catches errors after React mounts; these listeners sit above it and run
// from the very top of the bundle, so they also catch pre-mount exceptions and
// failed script/chunk loads (capture phase).
//
// Scoped to the boot window: stopEarlyErrorCapture() is called once the app
// renders successfully, so we don't log (or pay for) every runtime error for
// the whole session — only the ones that can produce a blank start.
//
// Purely observational: it does not preventDefault, so errors propagate exactly
// as before and the user experience is unchanged.
import { Logger } from "@touchtech/web-logging";

import { LocalStorageKeys } from "@is/constants";
import { LocalStorageService } from "@is/services";

function readItem(key: LocalStorageKeys): string | undefined {
  try {
    const value = LocalStorageService.getItem(key);
    return typeof value === "string" && value.length > 0 ? value : undefined;
  } catch {
    return undefined;
  }
}

function identity() {
  return {
    "tenant-id": readItem(LocalStorageKeys.TenantId),
    "device-id": readItem(LocalStorageKeys.DeviceId),
    "session-id": readItem(LocalStorageKeys.SessionId),
    appVersion: readItem(LocalStorageKeys.Version),
  };
}

let errorHandler: ((event: Event) => void) | null = null;
let rejectionHandler: ((event: PromiseRejectionEvent) => void) | null = null;

export function installEarlyErrorCapture(): void {
  try {
    if (errorHandler || typeof window === "undefined") return;

    errorHandler = (event: Event) => {
      try {
        const errorEvent = event as ErrorEvent;
        const target = event.target as
          | (HTMLElement & { src?: string; href?: string })
          | null;
        // A resource load failure (script/chunk/img) surfaces as an error
        // event whose target is the element and which has no message.
        const isResourceError =
          !!target && target !== (window as unknown) && !errorEvent.message;

        Logger.instance.error("Early startup error", {
          ...identity(),
          kind: isResourceError ? "resource" : "exception",
          message: errorEvent.message,
          source: errorEvent.filename,
          line: errorEvent.lineno,
          column: errorEvent.colno,
          stack: errorEvent.error?.stack,
          resource: isResourceError ? target?.src || target?.href : undefined,
        });
      } catch {
        // the handler must never throw
      }
    };

    rejectionHandler = (event: PromiseRejectionEvent) => {
      try {
        const reason = event.reason as
          | { message?: string; stack?: string }
          | undefined;

        Logger.instance.error("Early startup unhandled rejection", {
          ...identity(),
          message: reason?.message ?? String(event.reason),
          stack: reason?.stack,
        });
      } catch {
        // the handler must never throw
      }
    };

    // capture phase so resource (script/chunk) load failures are caught too
    window.addEventListener("error", errorHandler, true);
    window.addEventListener("unhandledrejection", rejectionHandler);
  } catch {
    // telemetry must never break app startup
  }
}

export function stopEarlyErrorCapture(): void {
  try {
    if (errorHandler) {
      window.removeEventListener("error", errorHandler, true);
      errorHandler = null;
    }
    if (rejectionHandler) {
      window.removeEventListener("unhandledrejection", rejectionHandler);
      rejectionHandler = null;
    }
  } catch {
    // best-effort
  }
}
